import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Phone, Video, Clock, PhoneOff } from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CallHistoryPanel = ({ onClose, onCallSelect }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCallHistory();
  }, []);

  const fetchCallHistory = async () => {
    try {
      const response = await axios.get(`${API}/calls/history`);
      setCalls(response.data);
    } catch (error) {
      console.error('Error fetching call history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCallIcon = (callType, status) => {
    if (callType === 'video') return <Video className="w-5 h-5" />;
    if (callType === 'phone') return <Phone className="w-5 h-5" />;
    return <Phone className="w-5 h-5" />;
  };

  const getCallStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#00FF66';
      case 'missed': return '#FF2A2A';
      case 'failed': return '#FFE800';
      default: return '#6B7280';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#08080C] rounded-3xl shadow-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-white/10"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Call History
          </h2>
          <button
            data-testid="close-call-history-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : calls.length > 0 ? (
            calls.map((call) => (
              <div
                key={call.id}
                data-testid={`call-history-item-${call.id}`}
                className="p-4 rounded-xl hover:bg-white/5 transition-all cursor-pointer border border-white/5"
                onClick={() => call.status === 'completed' && onCallSelect && onCallSelect(call)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: `${getCallStatusColor(call.status)}15`, color: getCallStatusColor(call.status) }}
                  >
                    {getCallIcon(call.call_type, call.status)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm truncate text-white">
                        {call.participants.filter(p => p.name).map(p => p.name).join(', ')}
                      </p>
                      <span className="text-xs ml-2 text-gray-500">
                        {formatDistanceToNow(new Date(call.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="capitalize">{call.call_type}</span>
                      <span>•</span>
                      <span className="capitalize" style={{ color: getCallStatusColor(call.status) }}>
                        {call.status}
                      </span>
                      {call.duration && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(call.duration)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <PhoneOff className="w-12 h-12 mb-3 text-gray-600" />
              <p className="text-sm font-medium text-gray-500">
                No call history yet
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CallHistoryPanel;